
import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { supabase, type BlogPost } from '../../../utils/supabase';

interface RelatedPostsProps {
  currentPostId: string;
  category: string;
}

export default function RelatedPosts({ currentPostId, category }: RelatedPostsProps) {
  const [posts, setPosts] = useState<BlogPost[]>([]);

  useEffect(() => {
    fetchRelated();
  }, [currentPostId, category]);

  const fetchRelated = async () => {
    try {
      const { data, error } = await supabase
        .from('blog_posts')
        .select('*')
        .eq('is_published', true)
        .eq('category', category)
        .neq('id', currentPostId)
        .order('views', { ascending: false })
        .limit(3);

      if (error) throw error;

      if (data && data.length < 3) {
        const ids = [currentPostId, ...data.map((p) => p.id)];
        const { data: extra, error: extraError } = await supabase
          .from('blog_posts')
          .select('*')
          .eq('is_published', true)
          .not('id', 'in', `(${ids.join(',')})`)
          .order('views', { ascending: false })
          .limit(3 - data.length);

        if (extraError) throw extraError;

        setPosts([...data, ...(extra || [])]);
      } else {
        setPosts(data || []);
      }
    } catch (error) {
      console.error('Erro ao buscar posts relacionados:', error);
      setPosts([]); // Ensure component stays stable on error
    }
  };

  if (posts.length === 0) return null;

  return (
    <section className="py-16 bg-neutral-50">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <h2 className="text-2xl font-bold text-neutral-900 mb-8 text-center">
          Artigos Relacionados
        </h2>
        <div className="grid md:grid-cols-3 gap-6">
          {posts.map((post) => (
            <Link
              key={post.id}
              to={`/blog/${post.slug}`}
              className="group bg-white rounded-xl overflow-hidden shadow-soft hover:shadow-hover transition-all cursor-pointer border border-neutral-100"
            >
              <div className="relative h-40 overflow-hidden">
                <img
                  src={post.image_url}
                  alt={post.title}
                  className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                />
                <div className="absolute top-3 left-3">
                  <span className="px-3 py-1 bg-white/90 backdrop-blur-sm text-primary text-xs font-semibold rounded-full">
                    {post.category}
                  </span>
                </div>
              </div>
              <div className="p-5">
                <h3 className="text-sm font-bold text-neutral-900 mb-2 group-hover:text-primary transition-colors line-clamp-2">
                  {post.title}
                </h3>
                <p className="text-neutral-500 text-xs line-clamp-2">{post.excerpt}</p>
                <div className="flex items-center gap-3 mt-3 text-xs text-neutral-400">
                  <span className="flex items-center gap-1">
                    <i className="ri-time-line"></i>
                    {post.reading_time} min
                  </span>
                  <span className="flex items-center gap-1">
                    <i className="ri-eye-line"></i>
                    {post.views}
                  </span>
                </div>
              </div>
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
}
